import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Target, Ticket, Package, Users, TrendingUp, AlertTriangle, CheckCircle, Clock } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const STAGE_COLORS = { lead: '#3B82F6', qualified: '#8B5CF6', proposal: '#F59E0B', won: '#10B981', lost: '#EF4444' };
const PRIO_COLORS = { low: '#94A3B8', medium: '#3B82F6', high: '#F59E0B', critical: '#EF4444' };

export default function DashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/reports/dashboard').then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>;
  if (!data) return <div className="text-muted-foreground text-center py-12">Failed to load dashboard</div>;

  const stageData = Object.entries(data.opportunities.stage_distribution || {}).map(([k, v]) => ({ name: k.charAt(0).toUpperCase() + k.slice(1), value: v, fill: STAGE_COLORS[k] }));
  const prioData = Object.entries(data.tickets.priority_distribution || {}).map(([k, v]) => ({ name: k.charAt(0).toUpperCase() + k.slice(1), value: v, fill: PRIO_COLORS[k] }));

  const metrics = [
    { title: 'Pipeline Value', value: `$${(data.opportunities.pipeline_value || 0).toLocaleString()}`, icon: TrendingUp, desc: `${data.opportunities.total} opportunities`, color: 'text-primary' },
    { title: 'Won Revenue', value: `$${(data.opportunities.won_value || 0).toLocaleString()}`, icon: CheckCircle, desc: `${data.opportunities.conversion_rate}% conversion`, color: 'text-success' },
    { title: 'Open Tickets', value: data.tickets.open, icon: Ticket, desc: `${data.tickets.total} total tickets`, color: 'text-warning' },
    { title: 'Customers', value: data.customers.total, icon: Users, desc: `${data.inventory.total_products} products`, color: 'text-chart-5' },
  ];

  return (
    <div data-testid="dashboard-page" className="space-y-6">
      <div>
        <h1 className="font-heading text-2xl font-bold tracking-tight">Dashboard</h1>
        <p className="text-sm text-muted-foreground mt-1">Overview of your CRM performance</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {metrics.map((m, i) => (
          <Card key={i} className="metric-card border border-border" style={{ animationDelay: `${i * 80}ms` }}>
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{m.title}</p>
                  <p className="text-2xl font-bold font-heading mt-1">{m.value}</p>
                  <p className="text-xs text-muted-foreground mt-1">{m.desc}</p>
                </div>
                <div className={`h-10 w-10 rounded-md bg-muted flex items-center justify-center ${m.color}`}>
                  <m.icon size={20} strokeWidth={1.5} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border border-border">
          <CardHeader className="pb-2"><CardTitle className="text-base font-heading">Sales Pipeline</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={stageData} margin={{ top: 5, right: 5, left: -10, bottom: 5 }}>
                <XAxis dataKey="name" tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ borderRadius: 6, border: '1px solid hsl(var(--border))', background: 'hsl(var(--card))' }} />
                <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                  {stageData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="border border-border">
          <CardHeader className="pb-2"><CardTitle className="text-base font-heading">Ticket Priorities</CardTitle></CardHeader>
          <CardContent>
            <div className="flex items-center gap-6">
              <ResponsiveContainer width="50%" height={220}>
                <PieChart>
                  <Pie data={prioData} dataKey="value" cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={3}>
                    {prioData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-3">
                {prioData.map(p => (
                  <div key={p.name} className="flex items-center gap-2">
                    <div className="h-3 w-3 rounded-full" style={{ background: p.fill }} />
                    <span className="text-sm">{p.name}</span>
                    <span className="text-sm font-semibold ml-auto">{p.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="border border-border">
          <CardHeader className="pb-2"><CardTitle className="text-base font-heading">SLA Compliance</CardTitle></CardHeader>
          <CardContent>
            <div className="text-3xl font-bold font-heading">{data.tickets.sla_compliance}%</div>
            <p className="text-xs text-muted-foreground mt-1">{data.tickets.overdue} overdue tickets</p>
            <div className="mt-3 h-2 bg-muted rounded-full overflow-hidden">
              <div className="h-full bg-success rounded-full transition-all" style={{ width: `${data.tickets.sla_compliance}%` }} />
            </div>
          </CardContent>
        </Card>

        <Card className="border border-border">
          <CardHeader className="pb-2"><CardTitle className="text-base font-heading">Ticket Status</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-3">
              <div className="flex items-center gap-2"><Clock size={14} className="text-primary" /><span className="text-sm">Open: {data.tickets.open}</span></div>
              <div className="flex items-center gap-2"><TrendingUp size={14} className="text-warning" /><span className="text-sm">In Progress: {data.tickets.in_progress}</span></div>
              <div className="flex items-center gap-2"><AlertTriangle size={14} className="text-destructive" /><span className="text-sm">Escalated: {data.tickets.escalated}</span></div>
              <div className="flex items-center gap-2"><CheckCircle size={14} className="text-success" /><span className="text-sm">Closed: {data.tickets.closed}</span></div>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-border">
          <CardHeader className="pb-2"><CardTitle className="text-base font-heading">Inventory</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex justify-between text-sm"><span>Total Assets</span><span className="font-semibold">{data.inventory.total_assets}</span></div>
              <div className="flex justify-between text-sm"><span>Available</span><span className="font-semibold text-success">{data.inventory.available_assets}</span></div>
              <div className="flex justify-between text-sm"><span>Deployed</span><span className="font-semibold text-primary">{data.inventory.deployed_assets}</span></div>
              {data.inventory.low_stock_products?.length > 0 && (
                <div className="mt-3 pt-3 border-t border-border">
                  <p className="text-xs font-medium text-destructive flex items-center gap-1"><AlertTriangle size={12} /> Low Stock Alerts</p>
                  {data.inventory.low_stock_products.slice(0, 3).map(p => (
                    <p key={p.product_id} className="text-xs text-muted-foreground mt-1">{p.name}: {p.stock_level} left</p>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
